import axios from "axios";
import { api } from "./client";
import {
  ApiResponse,
  Component,
  Page,
  PageSnapshot,
  Project,
  ProjectStatus,
  User,
} from "@/types";

type ProjectImportPage = {
  id?: string;
  name: string;
  components?: Component[];
  isDefault?: boolean;
  order?: number;
  template?: string;
};

export type PersistedProjectState = {
  components?: Component[];
  pages?: Array<Page | PageSnapshot>;
  settings?: Project["settings"] | Record<string, unknown>;
  metadata?: Record<string, unknown>;
  isMultiPage?: boolean;
  schemaVersion?: Project["schemaVersion"];
  lastModified?: Date | string;
  snapshotName?: string;
};

export interface CreateProjectRequest extends Record<string, unknown> {
  name: string;
  description?: string;
  template?: string;
  schemaVersion?: 1 | 2;
  isPrivate?: boolean;
  isMultiPage?: boolean;
  // Dual ownership model (Phase 1)
  owner_type?: "User" | "Organization";
  owner_id?: string;
  initialPages?: Array<{
    name: string;
    description?: string;
    template?: string;
    isDefault?: boolean;
  }>;
}

export interface UpdateProjectRequest extends Record<string, unknown> {
  name?: string;
  description?: string;
  status?: ProjectStatus;
  isPrivate?: boolean;
}

export interface ProjectListQuery {
  page?: number;
  limit?: number;
  status?: ProjectStatus;
  search?: string;
  sortBy?: "createdAt" | "updatedAt" | "name";
  sortOrder?: "asc" | "desc";
}

export interface ProjectListResponse {
  projects: Project[];
  total: number;
  page: number;
  totalPages: number;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface RawProjectListResponse {
  projects?: Project[];
  total?: number;
  page?: number;
  totalPages?: number;
  pagination?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
    pages?: number;
    hasNext?: boolean;
    hasPrev?: boolean;
  };
}

export interface ProjectCollaboratorRequest extends Record<string, unknown> {
  userId: string;
  role: "owner" | "editor" | "viewer";
}

export interface ProjectShareSettings {
  isPublic: boolean;
  allowComments: boolean;
  allowForks: boolean;
  shareLink?: string;
}

export interface ImportProjectRequest extends Record<string, unknown> {
  projectData: {
    id?: string;
    name: string;
    description?: string;
    pages: ProjectImportPage[];
    components?: Component[];
    isMultiPage?: boolean;
    schemaVersion?: 1 | 2;
    visibility?: string;
    settings?: Project["settings"];
    tags?: string[];
  };
  options?: {
    overrideName?: string;
    makePrivate?: boolean;
  };
}

/**
 * Owner context for scoped API requests (Phase 1: Dual Ownership Model)
 */
export interface OwnerContext {
  type: "user" | "organization";
  username?: string; // For user context
  orgSlug?: string; // For organization context
}

const isNonEmptyString = (value: string | undefined): value is string =>
  typeof value === "string" && value.trim().length > 0;

/**
 * Helper to build owner-scoped URL base
 * Phase 1: Dual Ownership Model
 */
function getOwnerScopedBase(ownerContext?: OwnerContext): string {
  if (!ownerContext) {
    return "/projects"; // Backward compatible: no owner context
  }

  if (ownerContext.type === "user") {
    if (isNonEmptyString(ownerContext.username)) {
      return `/users/${ownerContext.username}/projects`;
    }

    throw new Error("Invalid owner context: user context requires a username");
  }

  if (ownerContext.type === "organization") {
    if (isNonEmptyString(ownerContext.orgSlug)) {
      return `/orgs/${ownerContext.orgSlug}/projects`;
    }

    throw new Error(
      "Invalid owner context: organization context requires an orgSlug",
    );
  }

  throw new Error("Invalid owner context: unsupported owner type");
}

/**
 * Projects API service
 * Enhanced to support owner-scoped URLs (Phase 1: Dual Ownership Model)
 */
export class ProjectsApi {
  /**
   * Get all projects for the current user or organization
   * @param query - Query parameters for filtering and pagination
   * @param ownerContext - Optional owner context for scoped requests
   */
  static async getProjects(
    query: ProjectListQuery = {},
    ownerContext?: OwnerContext,
  ): Promise<ApiResponse<ProjectListResponse>> {
    const params = new URLSearchParams();

    if (query.page) params.append("page", query.page.toString());
    if (query.limit) params.append("limit", query.limit.toString());
    if (query.status) params.append("status", query.status);
    if (query.search) params.append("search", query.search);
    if (query.sortBy) params.append("sortBy", query.sortBy);
    if (query.sortOrder) params.append("sortOrder", query.sortOrder);

    const base = getOwnerScopedBase(ownerContext);
    const response = await api.get<RawProjectListResponse>(
      `${base}?${params.toString()}`,
    );

    if (!response.data) {
      throw new Error("Failed to fetch projects: no data returned");
    }

    const projects = response.data.projects ?? [];
    const limit =
      response.data.pagination?.limit ?? query.limit ?? projects.length;
    const total =
      response.data.total ?? response.data.pagination?.total ?? projects.length;
    const page =
      response.data.page ?? response.data.pagination?.page ?? query.page ?? 1;
    const totalPages =
      response.data.totalPages ??
      response.data.pagination?.totalPages ??
      response.data.pagination?.pages ??
      (limit > 0 ? Math.ceil(total / limit) : 1);

    return {
      ...response,
      data: {
        projects,
        total,
        page,
        totalPages,
        pagination: {
          page,
          limit,
          total,
          totalPages,
          hasNext: response.data.pagination?.hasNext ?? page < totalPages,
          hasPrev: response.data.pagination?.hasPrev ?? page > 1,
        },
      },
    };
  }

  /**
   * Get a specific project by ID
   */
  static async getProject(projectId: string): Promise<ApiResponse<Project>> {
    try {
      const response = await api.get<Project>(`/projects/${projectId}`);
      return response;
    } catch (error) {
      console.error("❌ ProjectsApi.getProject - Error:", { projectId, error });
      throw error;
    }
  }

  /**
   * Create a new project
   * @param data - Project creation data
   * @param ownerContext - Optional owner context for scoped requests
   */
  static async createProject(
    data: CreateProjectRequest,
    ownerContext?: OwnerContext,
  ): Promise<ApiResponse<Project>> {
    const base = getOwnerScopedBase(ownerContext);
    return api.post<Project, CreateProjectRequest>(base, data);
  }

  /**
   * Import a project from JSON data
   */
  static async importProject(
    data: ImportProjectRequest,
  ): Promise<ApiResponse<Project>> {
    return api.post<Project, ImportProjectRequest>("/projects/import", data);
  }

  /**
   * Update an existing project
   */
  static async updateProject(
    projectId: string,
    data: UpdateProjectRequest,
  ): Promise<ApiResponse<Project>> {
    return api.patch<Project, UpdateProjectRequest>(
      `/projects/${projectId}`,
      data,
    );
  }

  /**
   * Delete a project
   */
  static async deleteProject(projectId: string): Promise<ApiResponse<void>> {
    return api.delete<void>(`/projects/${projectId}`);
  }

  /**
   * Duplicate a project
   */
  static async duplicateProject(
    projectId: string,
    name?: string,
  ): Promise<ApiResponse<Project>> {
    return api.post<Project, { name?: string }>(
      `/projects/${projectId}/duplicate`,
      { name },
    );
  }

  /**
   * Get project collaborators
   */
  static async getProjectCollaborators(
    projectId: string,
  ): Promise<ApiResponse<User[]>> {
    return api.get<User[]>(`/projects/${projectId}/collaborators`);
  }

  /**
   * Add a collaborator to a project
   */
  static async addCollaborator(
    projectId: string,
    data: ProjectCollaboratorRequest,
  ): Promise<ApiResponse<void>> {
    return api.post<void, ProjectCollaboratorRequest>(
      `/projects/${projectId}/collaborators`,
      data,
    );
  }

  /**
   * Remove a collaborator from a project
   */
  static async removeCollaborator(
    projectId: string,
    userId: string,
  ): Promise<ApiResponse<void>> {
    return api.delete<void>(`/projects/${projectId}/collaborators/${userId}`);
  }

  /**
   * Update collaborator role
   */
  static async updateCollaboratorRole(
    projectId: string,
    userId: string,
    role: string,
  ): Promise<ApiResponse<void>> {
    return api.patch<void, { role: string }>(
      `/projects/${projectId}/collaborators/${userId}`,
      {
        role,
      },
    );
  }

  /**
   * Get project sharing settings
   */
  static async getShareSettings(
    projectId: string,
  ): Promise<ApiResponse<ProjectShareSettings>> {
    return api.get<ProjectShareSettings>(`/projects/${projectId}/share`);
  }

  /**
   * Update project sharing settings
   */
  static async updateShareSettings(
    projectId: string,
    settings: Partial<ProjectShareSettings>,
  ): Promise<ApiResponse<ProjectShareSettings>> {
    return api.patch<ProjectShareSettings, Partial<ProjectShareSettings>>(
      `/projects/${projectId}/share`,
      settings,
    );
  }

  /**
   * Generate a shareable link for a project
   */
  static async generateShareLink(
    projectId: string,
  ): Promise<ApiResponse<{ shareLink: string }>> {
    return api.post<{ shareLink: string }>(`/projects/${projectId}/share/link`);
  }

  /**
   * Revoke a shareable link for a project
   */
  static async revokeShareLink(projectId: string): Promise<ApiResponse<void>> {
    return api.delete<void>(`/projects/${projectId}/share/link`);
  }

  /**
   * Get recent projects for the current user or organization
   * @param limit - Maximum number of recent projects to return
   * @param ownerContext - Optional owner context for scoped requests
   */
  static async getRecentProjects(
    limit: number = 5,
    ownerContext?: OwnerContext,
  ): Promise<ApiResponse<Project[]>> {
    const base = getOwnerScopedBase(ownerContext);
    return api.get<Project[]>(`${base}/recent?limit=${limit}`);
  }

  /**
   * Get project templates
   */
  static async getTemplates(): Promise<ApiResponse<Project[]>> {
    return api.get<Project[]>("/projects/templates");
  }

  /**
   * Create project from template
   */
  static async createFromTemplate(
    templateId: string,
    name: string,
  ): Promise<ApiResponse<Project>> {
    return api.post<Project, { templateId: string; name: string }>(
      "/projects/from-template",
      { templateId, name },
    );
  }

  /**
   * Save project state (components, settings, pages, etc.)
   */
  static async saveProject(
    projectId: string,
    data: PersistedProjectState,
  ): Promise<ApiResponse<void>> {
    try {
      // Test JSON serialization before sending
      try {
        JSON.stringify(data);
      } catch (serializationError) {
        console.error("❌ Data serialization failed:", serializationError);
        throw new Error(
          `Data serialization failed: ${serializationError instanceof Error ? serializationError.message : String(serializationError)}`,
        );
      }

      // Add retry logic for 500 errors
      let lastError: unknown;
      const maxRetries = 2;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const response = await api.post<void>(
            `/projects/${projectId}/save`,
            data,
          );
          return response;
        } catch (attemptError: unknown) {
          lastError = attemptError;

          console.warn(`❌ Save attempt ${attempt} failed:`, {
            status: axios.isAxiosError(attemptError)
              ? attemptError.response?.status
              : undefined,
            message:
              attemptError instanceof Error
                ? attemptError.message
                : String(attemptError),
            data: axios.isAxiosError(attemptError)
              ? attemptError.response?.data
              : undefined,
          });

          // Only retry on 500 errors, not on client errors (400-499)
          if (
            axios.isAxiosError(attemptError) &&
            attemptError.response?.status === 500 &&
            attempt < maxRetries
          ) {
            console.log(
              `🔄 Retrying save in 1000ms (attempt ${attempt + 1}/${maxRetries})`,
            );
            await new Promise((resolve) => setTimeout(resolve, 1000));
            continue;
          }

          // If it's not a 500 error or we've exhausted retries, break
          break;
        }
      }

      // If we get here, all retries failed
      throw lastError;
    } catch (error) {
      const responseDetails =
        axios.isAxiosError(error) && error.response
          ? {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data,
            }
          : null;

      // Log detailed error information
      console.error("❌ ProjectsApi.saveProject - Error details:", {
        projectId,
        error: error instanceof Error ? error.message : error,
        stack: error instanceof Error ? error.stack : null,
        response: responseDetails,
      });
      throw error;
    }
  }

  /**
   * Auto-save project (used for periodic saves)
   */
  static async autoSaveProject(
    projectId: string,
    data: PersistedProjectState,
  ): Promise<ApiResponse<void>> {
    try {
      const response = await api.post<void>(
        `/projects/${projectId}/autosave`,
        data,
      );
      return response;
    } catch (error) {
      const responseDetails =
        axios.isAxiosError(error) && error.response
          ? {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data,
            }
          : null;

      console.error("❌ ProjectsApi.autoSaveProject - Error details:", {
        projectId,
        error: error instanceof Error ? error.message : error,
        response: responseDetails,
      });
      throw error;
    }
  }

  /**
   * Get project history/versions
   */
  static async getProjectHistory(
    projectId: string,
  ): Promise<ApiResponse<Project["versions"]>> {
    return api.get<Project["versions"]>(`/projects/${projectId}/history`);
  }

  /**
   * Restore project to a specific version
   */
  static async restoreVersion(
    projectId: string,
    versionId: string,
  ): Promise<ApiResponse<Project>> {
    return api.post<Project>(`/projects/${projectId}/restore/${versionId}`);
  }
}

export default ProjectsApi;
